import { useStyleRegister } from '@ant-design/cssinjs';
import { ConfigProvider, theme } from 'antd';
import React from 'react';

const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'];
const mediaBreakpointOrder = breakpointOrder.slice(1);

const breakpointTokenMap = {
  sm: 'screenSM',
  md: 'screenMD',
  lg: 'screenLG',
  xl: 'screenXL',
  xxl: 'screenXXL',
  xxxl: 'screenXXXL',
};

const gapTokenMap = {
  small: 'paddingSM',
  middle: 'padding',
  large: 'paddingLG',
};

const normalizeColumnCount = (count) => {
  const number = Number(count);
  if (!Number.isFinite(number) || number < 1) {
    return 1;
  }
  return Math.floor(number);
};

const createTemplateColumns = (count) => `repeat(${normalizeColumnCount(count)}, minmax(0, 1fr))`;

const hashString = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const mergeClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

const normalizeCSSGridColumns = (columns = 1) => {
  if (typeof columns === 'number') {
    return { xs: normalizeColumnCount(columns) };
  }

  if (!columns || typeof columns !== 'object') {
    return { xs: 1 };
  }

  const normalizedColumns = {};

  breakpointOrder.forEach((breakpoint) => {
    if (columns[breakpoint] === undefined) {
      return;
    }

    normalizedColumns[breakpoint] = normalizeColumnCount(columns[breakpoint]);
  });

  return Object.keys(normalizedColumns).length > 0 ? normalizedColumns : { xs: 1 };
};

const resolveCSSGridGap = (token, gap = 'middle') =>
  token[gapTokenMap[gap] ?? gapTokenMap.middle] ?? token.padding;

const normalizeMediaValue = (value) => (typeof value === 'number' ? `${value}px` : value);

const createCSSGridStyle = (token, columns, gap) => {
  const gridStyle = {
    alignItems: 'stretch',
    display: 'grid',
    gap: resolveCSSGridGap(token, gap),
    gridTemplateColumns: createTemplateColumns(columns.xs ?? 1),
    minWidth: 0,
  };

  mediaBreakpointOrder.forEach((breakpoint) => {
    const columnCount = columns[breakpoint];
    const tokenName = breakpointTokenMap[breakpoint];
    const breakpointSize = token[tokenName];

    if (columnCount === undefined || breakpointSize === undefined) {
      return;
    }

    gridStyle[`@media (min-width: ${normalizeMediaValue(breakpointSize)})`] = {
      gridTemplateColumns: createTemplateColumns(columnCount),
    };
  });

  return gridStyle;
};

const createCSSGridClassName = (prefixCls, columns, gap) =>
  `${prefixCls}-${hashString(JSON.stringify({ columns, gap }))}`;

const CSSGrid = React.forwardRef((props, ref) => {
  const {
    children,
    className,
    columns = 1,
    gap = 'middle',
    prefixCls: customizePrefixCls,
    rootClassName,
    style: _style,
    ...restProps
  } = props;
  const { csp, direction, getPrefixCls } = React.useContext(ConfigProvider.ConfigContext);
  const { hashId, theme: tokenTheme, token } = theme.useToken();
  const prefixCls = getPrefixCls('skill-css-grid', customizePrefixCls);
  const normalizedColumns = normalizeCSSGridColumns(columns);
  const responsiveClassName = createCSSGridClassName(prefixCls, normalizedColumns, gap);

  useStyleRegister(
    {
      hashId,
      layer: {
        name: 'antd',
      },
      nonce: () => csp?.nonce,
      path: ['CSSGrid', prefixCls, responsiveClassName],
      theme: tokenTheme,
      token,
    },
    () => ({
      [`.${responsiveClassName}`]: createCSSGridStyle(token, normalizedColumns, gap),
    }),
  );

  return React.createElement(
    'div',
    {
      ...restProps,
      className: mergeClassNames(
        prefixCls,
        responsiveClassName,
        direction === 'rtl' && `${prefixCls}-rtl`,
        className,
        rootClassName,
        hashId,
      ),
      ref,
    },
    children,
  );
});

if (process.env.NODE_ENV !== 'production') {
  CSSGrid.displayName = 'CSSGrid';
}

export default CSSGrid;
